import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Data, Router} from '@angular/router';
import {environment} from 'src/environments/environment';
import {ManageAppService} from 'app/features/apps/services/manage-app.service';
import {AppPermissions} from 'app/features/apps/interfaces/app.interface';
import {AppUrls, DccModuleUrls, path} from 'app/core/url/app-urls.service';
import {AuthService} from 'app/core/services/auth.service';

export interface DccPermissionGuardData extends Data {
    permission: keyof AppPermissions;
}

@Injectable()
export class DccPermissionGuard {
    constructor(
        private auth: AuthService,
        private manageAppService: ManageAppService,
        public router: Router
    ) {}

    canActivate(next: ActivatedRouteSnapshot): Promise<boolean> | boolean {
        /* istanbul ignore next */
        if (!next.data.permission && !environment.production) {
            console.warn(`[DccPermissionGuard] 'permission' is required in route data `);
        }

        if (!next.params || !next.params.appId) {
            return false;
        }

        return this.hasPermission(next.params.appId, next.data.permission).then(async permission => {
            if (!permission) {
                console.debug(`[DccPermissionGuard] '${next.data.permission}' access is forbidden `);

                if (next.routeConfig.path === DccModuleUrls.MEDIATION_GROUPS) {
                    await this.navigateToPage(AppUrls.DCC_CONFIGURATIONS, next.params.appId);
                    return false;
                }

                return true;
            }

            if (next.routeConfig.path === DccModuleUrls.CONFIGURATIONS) {
                console.debug(
                    `[DccPermissionGuard] Permission is true, but access to page '${next.routeConfig.path}' is forbidden for DCC 3.0'`
                );

                if (this.auth.canViewNewMediationSettings) {
                    return this.auth.canManageConfigurationsInDcc ? true : this.router.navigateByUrl('/404', {skipLocationChange: false});
                }

                await this.navigateToPage(AppUrls.DCC_MEDIATION_GROUPS, next.params.appId);
                return false;
            }

            return true;
        });
    }

    async hasPermission(appId: string, permission: keyof AppPermissions): Promise<boolean> {
        const currentApp = await this.manageAppService.fetchApp(appId);
        return currentApp ? currentApp.permissions[permission] : false;
    }

    navigateToPage(page: string, params: string): Promise<boolean> {
        return this.router.navigate([path(page, params)]);
    }
}
